import React from 'react';
import styled from 'styled-components';
import { PlusIcon, SearchIcon } from '../../Layout/icons';
import {
    Container,
    Header,
    Button,
    FiltersContainer,
    TableContainer,
    Table,
    PaginationContainer,
    PageInfo,
    PageButtons,
    Actions
} from './styles';

const ClickableRow = styled.tr`
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

    &:hover td {
        background-color: ${({ theme, $disabled }) =>
            $disabled
                ? undefined
                : theme.colors?.primary?.light
                  ? `${theme.colors.primary.light}18`
                  : 'rgba(59, 130, 246, 0.08)'};
    }
`;

export default function CrudList({
    title,
    onNew,
    newButtonLabel,
    onSearch,
    filters,
    columns,
    data,
    loading,
    pagination,
    sorting,
    actions,
    tableMinWidth,
    /** Reserva altura fixa do corpo (ex.: seletor de clientes com 10 linhas). */
    minBodyRows = 0,
    tableLayoutFixed = false,
    /** Oculta o bloco de cabeçalho (título + Novo); útil quando o título já existe fora (ex.: modal). */
    hideHeader = false,
    /** Corpo da tabela rola dentro do card; paginação permanece visível (ex.: seletor de clientes). */
    viewportContained = false,
    /** Quando informado, a linha inteira dispara a seleção (modais de escolha). */
    onRowClick,
    isRowDisabled,
    getRowKey = (item, index) => item?.id ?? index,
}) {
    const actionColumnCount = actions && !onRowClick ? 1 : 0;
    const colSpan = columns.length + actionColumnCount;
    const bodyPadRows =
        !loading && minBodyRows > 0 && data.length > 0 && data.length < minBodyRows
            ? minBodyRows - data.length
            : 0;
    const padRowStyle = {
        height: 49,
        padding: 0,
        borderBottom: 'none',
        lineHeight: 0,
        fontSize: 0,
    };
    const renderPadRows = (prefix, count) =>
        Array.from({ length: count }, (_, index) => (
            <tr key={`${prefix}-pad-${index}`} aria-hidden="true">
                <td colSpan={colSpan} style={padRowStyle}>
                    {'\u00A0'}
                </td>
            </tr>
        ));
    const handleSort = (field) => {
        if (!sorting || !sorting.onSort) return;

        const isAsc = sorting.field === field && sorting.direction === 'asc';
        sorting.onSort(field, isAsc ? 'desc' : 'asc');
    };

    const getSortIcon = (field) => {
        if (!sorting || sorting.field !== field) return null;
        return sorting.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const containerStyle =
        viewportContained || minBodyRows > 0
            ? {
                  minWidth: 0,
                  maxWidth: '100%',
                  ...(viewportContained
                      ? {
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            gap: 12,
                        }
                      : {}),
              }
            : undefined;

    const tableContainerStyle = viewportContained
        ? { flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto' }
        : undefined;

    return (
        <Container style={containerStyle}>
            {!hideHeader && (
                <Header>
                    <h1>{title}</h1>
                    {onNew && (
                        <Button variant="primary" onClick={onNew}>
                            <PlusIcon />
                            {newButtonLabel || 'Novo'}
                        </Button>
                    )}
                </Header>
            )}

            {filters && (
                <FiltersContainer>
                    {filters}
                    {onSearch && (
                        <Button type="button" variant="primary" onClick={onSearch} style={{ height: '38px' }}>
                            <SearchIcon />
                            Pesquisar
                        </Button>
                    )}
                </FiltersContainer>
            )}

            <TableContainer style={tableContainerStyle}>
                <Table
                    style={{
                        minWidth: tableMinWidth || '600px',
                        tableLayout: tableLayoutFixed ? 'fixed' : undefined,
                        width: tableLayoutFixed ? '100%' : undefined,
                    }}
                >
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.accessor}
                                    onClick={() => col.sortable && handleSort(col.accessor)}
                                    style={{
                                        cursor: col.sortable ? 'pointer' : 'default',
                                        width: col.width,
                                        minWidth: col.minWidth,
                                        maxWidth: col.maxWidth,
                                    }}
                                >
                                    {col.header}
                                    {col.sortable && getSortIcon(col.accessor)}
                                </th>
                            ))}
                            {actions && !onRowClick && <th style={{ width: '140px' }}>Ações</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <>
                                <tr>
                                    <td colSpan={colSpan} style={{ textAlign: 'center' }}>
                                        Carregando...
                                    </td>
                                </tr>
                                {minBodyRows > 1 ? renderPadRows('loading', minBodyRows - 1) : null}
                            </>
                        ) : data.length === 0 ? (
                            <>
                                <tr>
                                    <td colSpan={colSpan} style={{ textAlign: 'center' }}>
                                        Nenhum registro encontrado
                                    </td>
                                </tr>
                                {minBodyRows > 1 ? renderPadRows('empty', minBodyRows - 1) : null}
                            </>
                        ) : (
                            <>
                                {data.map((item, index) => {
                                    const rowKey = getRowKey(item, index);
                                    const rowDisabled = isRowDisabled?.(item) === true;
                                    const RowComponent = onRowClick ? ClickableRow : 'tr';
                                    const handleRowActivate = () => {
                                        if (!onRowClick || rowDisabled) return;
                                        onRowClick(item);
                                    };
                                    return (
                                        <RowComponent
                                            key={rowKey}
                                            $disabled={onRowClick ? rowDisabled : undefined}
                                            onClick={onRowClick ? handleRowActivate : undefined}
                                            onKeyDown={
                                                onRowClick
                                                    ? (e) => {
                                                          if (rowDisabled) return;
                                                          if (e.key === 'Enter' || e.key === ' ') {
                                                              e.preventDefault();
                                                              handleRowActivate();
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            tabIndex={onRowClick && !rowDisabled ? 0 : undefined}
                                            role={onRowClick ? 'button' : undefined}
                                        >
                                            {columns.map((col) => (
                                                <td
                                                    key={`${rowKey}-${col.accessor}`}
                                                    style={{
                                                        width: col.width,
                                                        minWidth: col.minWidth,
                                                        maxWidth: col.maxWidth,
                                                        overflow: 'hidden',
                                                    }}
                                                    onClick={
                                                        col.stopRowClick
                                                            ? (e) => e.stopPropagation()
                                                            : undefined
                                                    }
                                                >
                                                    {col.render ? col.render(item) : item[col.accessor]}
                                                </td>
                                            ))}
                                            {actions && !onRowClick && (
                                                <td style={{ width: '140px' }}>
                                                    <Actions>
                                                        {actions(item)}
                                                    </Actions>
                                                </td>
                                            )}
                                        </RowComponent>
                                    );
                                })}
                                {bodyPadRows > 0 ? renderPadRows('data', bodyPadRows) : null}
                            </>
                        )}
                    </tbody>
                </Table>
            </TableContainer>

            {
                pagination && (
                    <PaginationContainer style={{ flexShrink: 0 }}>
                        <PageInfo>
                            Página {pagination.totalPages === 0 ? 0 : pagination.page + 1} de {pagination.totalPages || 0}
                        </PageInfo>
                        <PageButtons>
                            <Button
                                variant="outline"
                                disabled={pagination.totalPages === 0 || pagination.first}
                                onClick={() => pagination.onPageChange(pagination.page - 1)}
                            >
                                Anterior
                            </Button>
                            <Button
                                variant="outline"
                                disabled={pagination.totalPages === 0 || pagination.last}
                                onClick={() => pagination.onPageChange(pagination.page + 1)}
                            >
                                Próxima
                            </Button>
                        </PageButtons>
                    </PaginationContainer>
                )
            }
        </Container >
    );
}
